import React from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

/**
 * @typedef {Object} SandboxArtifact
 * @property {string} id
 * @property {string} filename
 * @property {string} url
 * @property {string} contentType
 * @property {number} sizeBytes
 */

const DOC_ICONS = {
  xlsx: '📊', xls: '📊', csv: '📊',
  docx: '📝', doc: '📝', rtf: '📝',
  pdf: '📕',
  pptx: '📽️', ppt: '📽️',
  png: '🖼️', jpg: '🖼️', jpeg: '🖼️', gif: '🖼️',
};

function pickIcon(filename) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  return DOC_ICONS[ext] || '📄';
}

function formatBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * In-chat card for one sandbox artifact. The whole row (except the
 * download link) is clickable to open the preview panel.
 *
 * @param {object} props
 * @param {SandboxArtifact} props.artifact
 * @param {boolean} props.isActive
 * @param {function(string): void} props.onSelect - called with artifact.id
 * @param {function(SandboxArtifact, Event): void} [props.onDownload] - optional
 *   authenticated download handler. When provided it is called instead of
 *   letting the browser follow the plain `<a download>` link. Falls back to
 *   native browser download when omitted (e.g. in tests that only check href /
 *   download attributes).
 */
const DocumentCard = ({ artifact, isActive, onSelect, onDownload }) => {
  const handleSelect = () => onSelect(artifact.id);

  const handleDownloadClick = (e) => {
    e.stopPropagation();
    if (onDownload) {
      onDownload(artifact, e);
    }
    // If onDownload is not provided the browser follows the plain <a download> link.
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelect(); }}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors cursor-pointer
        ${isActive
          ? 'border-emerald-500/40 bg-emerald-500/10'
          : 'border-[var(--app-border)] hover:border-[var(--app-accent)]/40 bg-[var(--app-surface)]/60'}`}
    >
      <span className="text-xl shrink-0">{pickIcon(artifact.filename)}</span>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-[var(--app-text)] truncate">
          {artifact.filename}
        </div>
        {isActive ? (
          <div className="text-[11px] text-emerald-400">Currently viewing</div>
        ) : (
          <div className="text-[11px] text-[var(--app-text-secondary)]">
            {formatBytes(artifact.sizeBytes)}
          </div>
        )}
      </div>
      <a
        href={artifact.url}
        download
        onClick={handleDownloadClick}
        className="shrink-0 p-1.5 rounded-md border border-[var(--app-border)] hover:bg-[var(--app-surface)] text-[var(--app-text-secondary)] hover:text-[var(--app-text)]"
        aria-label="Download"
      >
        <ArrowDownTrayIcon className="w-4 h-4" aria-hidden="true" />
      </a>
    </div>
  );
};

export default DocumentCard;
